import React, { useEffect, useMemo, useState } from 'react';
import UserActionsMenu from './UserActionsMenu';
import buildImageUrl from '../../config/imageUrlHelper';

const UserTable = ({ 
  usuarios, 
  usuariosConectados, 
  permisoModeracion, 
  toggleAcciones, 
  accionesUsuario, 
  setAccionMenu,
  handleActualizarRol, 
  confirmarAccion,
  onUserClick 
}) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [visibleEmails, setVisibleEmails] = useState({});

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const onChange = (event) => setIsMobile(event.matches);
    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', onChange);

    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  const getStatus = (usuario) => {
    const baneadoRaw = usuario.baneado === 1 || usuario.baneado === '1' || usuario.baneado === true;
    const fechaExpiracion = usuario.fecha_baneo ? new Date(usuario.fecha_baneo) : null;
    const timeoutActivo = !fechaExpiracion || Number.isNaN(fechaExpiracion.getTime()) || fechaExpiracion > new Date();
    const baneado = baneadoRaw && timeoutActivo;

    // Prioridad obligatoria: Baneado > Activo > Inactivo
    if (baneado) {
      return {
        label: 'Baneado',
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      };
    }

    if (usuariosConectados.has(String(usuario.id))) {
      return {
        label: 'Activo',
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      };
    }

    return {
      label: 'Inactivo',
      className: 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
    };
  };

  const toggleEmail = (userId) => {
    setVisibleEmails((prev) => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const getMaskedEmail = (email) => {
    const value = String(email || '').trim();
    if (!value.includes('@')) return '***';

    const domain = value.split('@')[1] || 'gmail.com';
    return `***@${domain}`;
  };

  const resolveAvatar = (usuario) => {
    const imageUrl = buildImageUrl(usuario.foto_perfil);

    if (imageUrl) {
      return (
        <img
          src={imageUrl}
          alt={`Avatar de ${usuario.username}`}
          className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-gray-500"
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      );
    }

    return (
      <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm font-semibold text-gray-700 dark:text-gray-200">
        {(usuario.username || 'U').charAt(0).toUpperCase()}
      </div>
    );
  };

  const commonRows = useMemo(() => usuarios.map((usuario) => {
    const status = getStatus(usuario);
    const emailVisible = visibleEmails[usuario.id];

    return {
      usuario,
      status,
      emailVisible,
      emailText: emailVisible ? usuario.email : getMaskedEmail(usuario.email)
    };
  }), [usuarios, usuariosConectados, visibleEmails]);

  const renderDesktopRow = ({ usuario, status, emailText }) => (
    <tr
      key={usuario.id}
      className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
      onClick={() => onUserClick(usuario)}
    >
      <td className="py-3 px-4">{resolveAvatar(usuario)}</td>
      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{usuario.username}</td>
      <td className="py-3 px-4">
        <button
          type="button"
          className="text-left text-gray-700 dark:text-gray-200 hover:underline"
          onClick={(event) => {
            event.stopPropagation();
            toggleEmail(usuario.id);
          }}
        >
          {emailText}
        </button>
      </td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${status.className}`}>
          {status.label}
        </span>
      </td>
      <td className="py-3 px-4 relative" onClick={(event) => event.stopPropagation()}>
        <UserActionsMenu
          usuario={usuario}
          permisoModeracion={permisoModeracion}
          toggleAcciones={toggleAcciones}
          accionesUsuario={accionesUsuario}
          setAccionMenu={setAccionMenu}
          handleActualizarRol={handleActualizarRol}
          confirmarAccion={confirmarAccion}
        />
      </td>
    </tr>
  );

  if (isMobile) {
    return (
      <div className="space-y-3">
        {commonRows.map(({ usuario, status, emailText }) => (
          <div
            key={usuario.id}
            className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 p-3 shadow-sm"
            onClick={() => onUserClick(usuario)}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {resolveAvatar(usuario)}
                <div className="min-w-0">
                  <p className="font-semibold truncate dark:text-white">{usuario.username}</p>
                  <button
                    type="button"
                    className="text-xs text-left text-gray-600 dark:text-gray-200 hover:underline"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleEmail(usuario.id);
                    }}
                  >
                    {emailText}
                  </button>
                </div>
              </div>
              <div onClick={(event) => event.stopPropagation()}>
                <UserActionsMenu
                  usuario={usuario}
                  permisoModeracion={permisoModeracion}
                  toggleAcciones={toggleAcciones}
                  accionesUsuario={accionesUsuario}
                  setAccionMenu={setAccionMenu}
                  handleActualizarRol={handleActualizarRol}
                  confirmarAccion={confirmarAccion}
                />
              </div>
            </div>

            <div className="mt-3">
              <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${status.className}`}>
                {status.label}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg shadow">
      <table className="min-w-full bg-white dark:bg-gray-700 overflow-hidden">
        <thead className="bg-gray-100 dark:bg-gray-600">
          <tr>
            <th className="py-2 px-4 text-left">Foto</th>
            <th className="py-2 px-4 text-left">Usuario</th>
            <th className="py-2 px-4 text-left">Email</th>
            <th className="py-2 px-4 text-left">Estado</th>
            <th className="py-2 px-4 text-left">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {commonRows.map(renderDesktopRow)}
        </tbody>
      </table>
    </div>
  );
};

export default UserTable;
